import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  BarChart3, CalendarClock, ChevronRight, FileClock, Gem, History, Loader2,
  MessageCircle, Megaphone, ThumbsDown, ThumbsUp, Users
} from 'lucide-react'
import toast from 'react-hot-toast'
import TeamAvatar from './TeamAvatar'
import TeamHonors from './TeamHonors'
import AnimatedNumber from '../../../components/AnimatedNumber'
import {
  accentColor, cardNickColor, cardPrimaryColor, cardSecondaryColor,
  contentBgColor
} from '../utils/teamColors'
import { backgroundImageStyle, backgroundOverlayStyle } from '../utils/teamImages'
import { teamStatusLabel } from '../utils/teamFormat'
import { formatAgo } from '../../profile/utils/profileFormat'
import useAudioManager from '../../../hooks/useAudioManager'
import { hapticImpact } from '../../../utils/haptic'

/**
 * Hero-блок карточки банды (центровая композиция): аватар по центру в кольце
 * цвета банды с онлайн-точкой, название, голоса, мета-чипы вместо служебной
 * строки, казна крупной цифрой и сетка действий.
 *
 * Скрытые владельцем блоки приходят как `null` (`treasury: null`,
 * `contacts: null`) — это «скрыто», а не «пусто»; участник видит всё
 * независимо от тумблеров видимости. Казна показывается цифрой только когда
 * в ней есть кристаллы: у пустой казны нет визуального веса, участник
 * попадает в неё кнопкой действий.
 */
const TeamHeader = ({ team, onVote, isVotePending, isGuest }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { playSound } = useAudioManager({ select: 'select' })
  const colors = team.colors

  const accent = accentColor(colors)
  const primary = cardPrimaryColor(colors)
  const nick = cardNickColor(colors)
  const secondary = cardSecondaryColor(colors)
  const contentBg = contentBgColor(colors)
  const subtleSurface = `${primary}0D`
  const subtleBorder = `${primary}1F`

  const background = typeof team.background?.url === 'string' ? team.background : null
  const contacts = team.contacts || null
  const isMember = team.is_member === true
  const isOnline = (team.online_count ?? 0) > 0
  const hasTreasury = team.treasury != null

  // Уровень банды виден всем и всегда, при любых тумблерах видимости. На бэкенде
  // без миграции 409 ключа ещё нет — тогда бейдж не рисуем вовсе (level: 0 валиден:
  // «командных игр ещё не было»). Прогресс до следующего уровня в шапке не
  // дублируем — он живёт на странице статистики банды
  const level = Number.isFinite(team.level) ? team.level : null
  // Статистика и история игр: участнику всегда, чужому — по тумблеру show_stats
  const canViewStats = team.can_view_stats === true

  const handleVote = (value) => {
    if (isVotePending) return
    if (isGuest) return onVote(value)
    // can_vote: false — нет депозита, никогда не состоял в банде или голос
    // заморожен (сутки после выхода). Кнопка не «мертвая»: по тапу подсказка
    if (!team.can_vote) {
      toast(t('team.voteHint', 'Голосовать могут игроки с пополнением, которые состоят или состояли в банде'), { duration: 5000 })
      return
    }
    hapticImpact('light')
    // Повторное нажатие по своему голосу снимает его (value 0)
    onVote(team.my_vote === value ? 0 : value)
  }

  const openLink = (url) => {
    if (!url) return
    hapticImpact('light')
    const tg = window.Telegram?.WebApp
    if (tg?.openTelegramLink && /^https:\/\/(t\.me|telegram\.(me|dog))\//i.test(url)) {
      tg.openTelegramLink(url)
    } else if (tg?.openLink) {
      tg.openLink(url)
    } else {
      window.open(url, '_blank', 'noopener,noreferrer')
    }
  }

  const goTo = (path) => {
    playSound('select')
    navigate(path)
  }

  const chipClass = 'flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium'
  const tileClass = 'flex min-w-0 flex-col items-center justify-center gap-1 rounded-xl border px-2 py-2.5 text-[11px] font-semibold transition-all active:scale-[0.98]'

  // Вторичные действия сеткой: состав колонок зависит от контактов и членства.
  // Отдельной плитки «Казна» нет — в казну ведёт сама строка с суммой
  const tiles = [
    contacts?.channel && { key: 'channel', icon: Megaphone, label: t('team.contactChannel', 'Канал'), onClick: () => openLink(contacts.channel) },
    // Страховка: участник всегда видит казну, но если сумма не пришла — вход плиткой
    isMember && !hasTreasury && { key: 'treasury', icon: Gem, label: t('team.treasuryShort', 'Казна'), onClick: () => goTo('/teams/treasury') },
    // Статистика и история командных игр — по can_view_stats: при false ручки
    // ответят 403 stats_hidden, поэтому кнопок быть не должно
    canViewStats && { key: 'stats', icon: BarChart3, label: t('team.stats.short', 'Статистика'), onClick: () => goTo(`/teams/${team.id}/stats`) },
    canViewStats && { key: 'games', icon: History, label: t('team.stats.gamesShort', 'Игры'), onClick: () => goTo(`/teams/${team.id}/games`) },
    isMember && { key: 'audit', icon: FileClock, label: t('team.auditShort', 'История'), onClick: () => goTo('/teams/audit') }
  ].filter(Boolean)

  // Участник из строки казны уходит в журнал операций; гостю она только инфо
  const TreasuryTag = isMember ? 'button' : 'div'
  const treasuryRowClass = 'mt-4 flex w-full items-center justify-between gap-3 rounded-xl border px-3.5 py-2.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]'
  const treasuryStyle = { backgroundColor: subtleSurface, borderColor: subtleBorder }
  const treasuryProps = isMember
    ? {
        onClick: () => goTo('/teams/treasury'),
        'aria-label': t('team.treasury', 'В казне'),
        className: `${treasuryRowClass} text-left transition-all active:scale-[0.98]`,
        style: treasuryStyle
      }
    : { className: treasuryRowClass, style: treasuryStyle }

  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-white/10 px-5 pb-5 pt-6 shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_10px_15px_-3px_rgba(0,0,0,0.25),0_4px_6px_-4px_rgba(0,0,0,0.25)]"
      style={{ backgroundColor: contentBg, borderColor: subtleBorder }}
    >
      {/* Слои картинки-фона и затемнения (оформление банды) */}
      {background && (
        <>
          <div className="pointer-events-none absolute" style={backgroundImageStyle(background)} />
          <div className="pointer-events-none absolute inset-0" style={backgroundOverlayStyle(background)} />
        </>
      )}

      {/* Единственный тяжёлый blur карточки — мягкое свечение цвета банды за аватаром */}
      <div
        className="pointer-events-none absolute -top-20 left-1/2 h-48 w-48 -translate-x-1/2 rounded-full opacity-30 blur-3xl"
        style={{ backgroundColor: accent }}
      />

      {/* Hairline-градиент по верхней кромке в цвет банды */}
      <div
        className="pointer-events-none absolute inset-x-8 top-0 h-px"
        style={{ backgroundImage: `linear-gradient(90deg, transparent, ${accent}80, transparent)` }}
      />

      <div className="relative flex flex-col items-center text-center">
        {/* Аватар + онлайн-точка на углу. Рамку рисует сам TeamAvatar (цвет
            avatar_frame): свой внешний ринг сюда не добавляем — у него был бы
            другой радиус и рамка «пробивалась» бы наружу */}
        <div className="relative">
          <TeamAvatar team={team} size={88} />
          {isOnline && (
            <span
              className="absolute -right-1 -top-1 h-4 w-4 rounded-full border-2"
              style={{ borderColor: contentBg, backgroundColor: accent, boxShadow: `0 0 10px ${accent}` }}
              aria-hidden="true"
            />
          )}

          {/* Бейдж уровня банды — таблетка на нижней кромке аватара, как в профиле.
              Уровень банды растёт по кубической кривой от общего оборота состава,
              поэтому бывает трёх- и четырёхзначным: ширина тянется под число */}
          {level !== null && (
            <span
              className="absolute -bottom-1.5 left-1/2 flex -translate-x-1/2 items-center gap-1 whitespace-nowrap rounded-full border px-2.5 py-1"
              style={{ backgroundColor: contentBg, borderColor: accent, boxShadow: `0 0 12px ${accent}55` }}
              title={t('team.level', 'Уровень банды')}
            >
              <span className="text-[9px] font-bold uppercase leading-none tracking-wide" style={{ color: secondary }}>
                {t('publicProfile.levelShort', 'LVL')}
              </span>
              <span className="text-xs font-bold leading-none tabular-nums" style={{ color: secondary }}>
                {level}
              </span>
            </span>
          )}
        </div>

        <h1 className="mt-3 max-w-full truncate text-2xl not-italic" style={{ color: nick }}>
          {team.name}
        </h1>

        {/* Описание — прямо под названием, как подпись банды, а не отдельной
            карточкой. Показывается целиком: бэкенд ограничивает его 500 символами */}
        {team.description && (
          <p
            className="mt-2 w-full max-w-[44ch] whitespace-pre-line break-words text-[13px] leading-relaxed"
            style={{ color: secondary }}
          >
            {team.description}
          </p>
        )}

        {/* Голоса сразу под названием: репутация — главное в шапке;
            числа видны всем, кнопки — по can_vote */}
        <div className="mt-2.5 flex items-center justify-center gap-2">
          <button
            onClick={() => handleVote(1)}
            disabled={isVotePending}
            className={`flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-semibold transition-all active:scale-[0.98] ${team.can_vote ? '' : 'cursor-default opacity-70'}`}
            style={{
              borderColor: team.my_vote === 1 ? accent : subtleBorder,
              color: secondary,
              backgroundColor: team.my_vote === 1 ? `${accent}14` : 'transparent'
            }}
            aria-label={t('team.like', 'Нравится')}
          >
            {isVotePending
              ? <Loader2 size={15} className="animate-spin" />
              : <ThumbsUp size={15} className={team.my_vote === 1 ? 'fill-current' : ''} style={{ color: team.my_vote === 1 ? accent : secondary }} />}
            <span className="tabular-nums">{team.likes ?? 0}</span>
          </button>

          <button
            onClick={() => handleVote(-1)}
            disabled={isVotePending}
            className={`flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-semibold transition-all active:scale-[0.98] ${team.can_vote ? '' : 'cursor-default opacity-70'}`}
            style={{
              borderColor: team.my_vote === -1 ? '#FF4B4B' : subtleBorder,
              color: secondary,
              backgroundColor: team.my_vote === -1 ? 'rgba(255,75,75,0.08)' : 'transparent'
            }}
            aria-label={t('team.dislike', 'Не нравится')}
          >
            <ThumbsDown
              size={15}
              className={team.my_vote === -1 ? 'fill-current' : ''}
              style={{ color: team.my_vote === -1 ? '#FF4B4B' : secondary }}
            />
            <span className="tabular-nums">{team.dislikes ?? 0}</span>
          </button>
        </div>

        {/* Мета-чипы: состав, онлайн, возраст — вместо серой служебной строки */}
        <div className="mt-2.5 flex flex-wrap items-center justify-center gap-1.5" style={{ color: secondary }}>
          <span className={`${chipClass} tabular-nums`} style={{ backgroundColor: subtleSurface, borderColor: subtleBorder }}>
            <Users size={12} style={{ color: accent }} />
            {team.members_count}/{team.max_members}
          </span>
          <span className={chipClass} style={{ backgroundColor: subtleSurface, borderColor: subtleBorder }}>
            <span
              className="h-1.5 w-1.5 rounded-full"
              style={isOnline
                ? { backgroundColor: accent, boxShadow: `0 0 8px ${accent}99` }
                : { backgroundColor: secondary }}
            />
            {t('team.onlineCount', '{{count}} онлайн', { count: team.online_count ?? 0 })}
          </span>
          {team.created_ago != null && (
            <span className={chipClass} style={{ backgroundColor: subtleSurface, borderColor: subtleBorder }}>
              <CalendarClock size={12} style={{ color: accent }} />
              {formatAgo(team.created_ago, t)}
            </span>
          )}
        </div>

        <div className="mt-3"><TeamHonors team={team} chipStyle={{ color: primary, backgroundColor: subtleSurface, borderColor: subtleBorder }} /></div>

        {/* Статус: активная банда плашки не требует */}
        {team.status && team.status !== 'active' && (
          <span className="mt-2.5 inline-flex rounded-full border border-red-500/40 bg-red-500/10 px-2.5 py-0.5 text-[11px] font-medium text-red-400">
            {teamStatusLabel(team.status, t)}
          </span>
        )}

        {/* Казна строкой-значением: сумма видна всегда, когда она не скрыта
            (`treasury: null` — скрыта владельцем, блок не рисуется вовсе, иначе
            ноль и «скрыто» выглядели бы одинаково). Участнику строка кликабельна,
            а весь текст строки использует пользовательский цвет подписей */}
        {hasTreasury && (
          <TreasuryTag {...treasuryProps}>
            <span className="flex shrink-0 items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider" style={{ color: secondary }}>
              <Gem size={13} style={{ color: accent }} />
              {t('team.treasury', 'В казне')}
            </span>
            <span className="flex min-w-0 items-center gap-1">
              <span className="truncate text-2xl font-bold leading-none tabular-nums" style={{ color: secondary }}>
                <AnimatedNumber value={team.treasury} duration={1} />
              </span>
              {isMember && <ChevronRight size={16} className="shrink-0" style={{ color: secondary }} />}
            </span>
          </TreasuryTag>
        )}

        {/* Главное действие — чат банды (contacts: null — скрыты владельцем или не заданы) */}
        {contacts?.chat && (
          <button
            onClick={() => openLink(contacts.chat)}
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl border px-4 py-3 text-sm font-bold transition-all active:scale-[0.98]"
            style={{ borderColor: `${accent}66`, color: secondary, backgroundColor: `${accent}14` }}
          >
            <MessageCircle size={16} style={{ color: accent }} />
            {t('team.contactChat', 'Чат банды')}
          </button>
        )}

        {/* Вторичные действия плитками: канал, казна и аудит */}
        {tiles.length > 0 && (
          <div className={`mt-2 grid w-full gap-2 ${tiles.length === 1 ? 'grid-cols-1' : tiles.length === 2 || tiles.length === 4 ? 'grid-cols-2' : 'grid-cols-3'}`}>
            {tiles.map(({ key, icon: Icon, label, onClick }) => (
              <button
                key={key}
                onClick={onClick}
                className={tileClass}
                style={{ color: secondary, backgroundColor: subtleSurface, borderColor: subtleBorder }}
              >
                <Icon size={16} style={{ color: accent }} />
                <span className="w-full truncate">{label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default TeamHeader
